import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Fab,
  Paper,
  LinearProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add,
  Delete,
  PhotoCamera,
  CameraAlt,
  MenuBook,
  LocalGroceryStore,
  Timer,
  People,
  Close,
  Save,
  Visibility,
  ArrowBack,
  Crop,
  Print,
  Share
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  addDoc, 
  getDocsFromServer,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { extractRecipeText } from '../utils/realOCR';
import ImageEditor from './ImageEditor';
import { adjustRecipeServings, adjustCookingTime } from '../utils/servingAdjuster';
import { shareRecipeLink } from '../utils/recipeShareHelper';

function RecipeManager({ familyId, onBack, addShoppingItem }) {
  const { currentUser } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [extractingText, setExtractingText] = useState(false);
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editedImageFile, setEditedImageFile] = useState(null);
  const [servingSize, setServingSize] = useState(4);
  const [originalServings, setOriginalServings] = useState(4);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmRecipeDelete, setConfirmRecipeDelete] = useState({ open: false, recipeId: null, collectionHint: null });
  
  // Recipe form state
  const [recipeForm, setRecipeForm] = useState({
    name: '',
    prepTime: '',
    cookTime: '',
    servings: '',
    difficulty: 'Easy',
    description: '',
    ingredients: [{ name: '', amount: '', unit: '', category: 'Pantry' }],
    instructions: [''],
    cookingTips: [''],
    tags: [],
    photoUrl: '',
    source: 'family' // 'family', 'photo', 'manual'
  });

  // Load family recipes
  const loadRecipes = async () => {
    if (!familyId) return;
    
    setLoading(true);
    try {
      // Load from both locations: old familyRecipes collection and new subcollection
      const recipesFromOldCollection = [];
      const recipesFromSubcollection = [];
      
      // Load from old collection (familyRecipes) with cache disabled
      const recipesRef = collection(db, 'familyRecipes');
      const q = query(
        recipesRef,
        where('familyId', '==', familyId),
        orderBy('createdAt', 'desc')
      );
      
      // Force fetch from server, bypassing cache
      const snapshot = await getDocsFromServer(q);
      recipesFromOldCollection.push(...snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      // Load from new subcollection (families/{familyId}/recipes)
      try {
        const subcollectionRef = collection(db, 'families', familyId, 'recipes');
        const subcollectionSnapshot = await getDocsFromServer(subcollectionRef);
        recipesFromSubcollection.push(...subcollectionSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().title || doc.data().name // Support both title and name fields
        })));
      } catch (subError) {
        console.log('No recipes in subcollection yet:', subError);
      }
      
      // Combine and deduplicate recipes by ID
      const allRecipes = [...recipesFromOldCollection, ...recipesFromSubcollection];
      const uniqueRecipes = [];
      const seenIds = new Set();
      
      for (const recipe of allRecipes) {
        if (!seenIds.has(recipe.id)) {
          seenIds.add(recipe.id);
          // Mark which collection it's from for deletion
          recipe._collection = recipesFromSubcollection.includes(recipe) ? 'subcollection' : 'old';
          uniqueRecipes.push(recipe);
        } else {
          console.log('Skipping duplicate recipe:', recipe.id);
        }
      }
      
      console.log('Loaded recipes from old collection:', recipesFromOldCollection.length);
      console.log('Loaded recipes from subcollection:', recipesFromSubcollection.length);
      console.log('Total recipes after deduplication:', uniqueRecipes.length);
      console.log('Recipe IDs:', uniqueRecipes.map(r => r.id));
      setRecipes(uniqueRecipes);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  const handleOpenDialog = (recipe = null) => {
    if (recipe) {
      setEditingRecipe(recipe);
      // Normalize recipe structure for both Burt recipes and manual recipes
      setRecipeForm({
        name: recipe.name || recipe.title || '',
        prepTime: recipe.prepTime || '',
        cookTime: recipe.cookTime || '',
        servings: recipe.servings || '',
        difficulty: recipe.difficulty || 'Easy',
        description: recipe.description || '',
        ingredients: Array.isArray(recipe.ingredients) 
          ? (typeof recipe.ingredients[0] === 'string' 
              ? recipe.ingredients.map(ing => ({ name: ing, amount: '', unit: '', category: 'Pantry' }))
              : recipe.ingredients)
          : [{ name: '', amount: '', unit: '', category: 'Pantry' }],
        instructions: Array.isArray(recipe.instructions) 
          ? recipe.instructions 
          : [''],
        cookingTips: recipe.cookingTips || recipe.tips || [],
        tags: recipe.tags || [],
        photoUrl: recipe.photoUrl || '',
        source: recipe.source || 'manual',
        fullContent: recipe.fullContent || '' // Preserve Burt's full response
      });
    } else {
      setEditingRecipe(null);
      setRecipeForm({
        name: '',
        prepTime: '',
        cookTime: '',
        servings: '',
        difficulty: 'Easy',
        description: '',
        ingredients: [{ name: '', amount: '', unit: '', category: 'Pantry' }],
        instructions: [''],
        cookingTips: [],
        tags: [],
        photoUrl: '',
        source: 'manual'
      });
    }
    setActiveTab(0);
    // View mode when opening an existing recipe; edit mode for new recipes
    setIsEditMode(!recipe);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecipe(null);
    setIsEditMode(false);
    setRecipeForm({
      name: '',
      prepTime: '',
      cookTime: '',
      servings: '',
      difficulty: 'Easy',
      description: '',
      ingredients: [{ name: '', amount: '', unit: '', category: 'Pantry' }],
      instructions: [''],
      cookingTips: [''],
      tags: [],
      photoUrl: '',
      source: 'manual'
    });
  };

  const handleSaveRecipe = async () => {
    if (!recipeForm.name.trim()) {
      alert('Please enter a recipe name');
      return;
    }

    setUploading(true);
    try {
      const recipeData = {
        ...recipeForm,
        familyId,
        createdBy: currentUser.uid,
        createdAt: editingRecipe ? editingRecipe.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        ingredients: recipeForm.ingredients.filter(ing => ing.name.trim()),
        instructions: recipeForm.instructions.filter(inst => inst.trim()),
        cookingTips: recipeForm.cookingTips.filter(tip => tip.trim())
      };

      if (editingRecipe) {
        // Update in the correct collection based on where it was loaded from
        const collectionPath = editingRecipe._collection === 'subcollection' 
          ? ['families', familyId, 'recipes']
          : ['familyRecipes'];
        
        console.log('Updating recipe in:', collectionPath.join('/'));
        await updateDoc(doc(db, ...collectionPath, editingRecipe.id), recipeData);
      } else {
        // New recipes go to the old collection for now (manual recipes)
        await addDoc(collection(db, 'familyRecipes'), recipeData);
      }

      handleCloseDialog();
      loadRecipes();
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Error saving recipe. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePrintRecipe = (recipe) => {
    // Create a print-friendly version of the recipe
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${recipe.name || recipe.title || 'Recipe'}</title>
          <style>
            @media print {
              @page { margin: 1in; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              line-height: 1.6;
            }
            h1 {
              color: #2e7d32;
              border-bottom: 3px solid #2e7d32;
              padding-bottom: 10px;
            }
            h2 {
              color: #388e3c;
              margin-top: 30px;
            }
            .recipe-info {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 15px;
              margin: 20px 0;
              padding: 15px;
              background: #f5f5f5;
              border-radius: 8px;
            }
            .info-item {
              display: flex;
              flex-direction: column;
            }
            .info-label {
              font-weight: bold;
              color: #666;
              font-size: 0.9em;
            }
            .info-value {
              color: #333;
              font-size: 1.1em;
            }
            .ingredients-list {
              list-style: none;
              padding: 0;
            }
            .ingredients-list li {
              padding: 8px;
              margin: 5px 0;
              background: #f9f9f9;
              border-left: 3px solid #4caf50;
            }
            .instructions-list {
              list-style: decimal;
              padding-left: 25px;
            }
            .instructions-list li {
              margin: 15px 0;
              padding-left: 10px;
            }
            .tips-list {
              background: #fff3cd;
              padding: 15px;
              border-left: 4px solid #ffc107;
              margin: 10px 0;
            }
            .tips-list li {
              margin: 8px 0;
            }
            .recipe-photo {
              max-width: 100%;
              height: auto;
              margin: 20px 0;
              border-radius: 8px;
            }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${recipe.name || recipe.title || 'Recipe'}</h1>
          
          ${recipe.photoUrl ? `<img src="${recipe.photoUrl}" alt="${recipe.name || recipe.title}" class="recipe-photo" />` : ''}
          
          ${recipe.description ? `<p><em>${recipe.description}</em></p>` : ''}
          
          <div class="recipe-info">
            ${recipe.prepTime ? `
              <div class="info-item">
                <span class="info-label">Prep Time</span>
                <span class="info-value">${recipe.prepTime}</span>
              </div>
            ` : ''}
            ${recipe.cookTime ? `
              <div class="info-item">
                <span class="info-label">Cook Time</span>
                <span class="info-value">${recipe.cookTime}</span>
              </div>
            ` : ''}
            ${recipe.servings ? `
              <div class="info-item">
                <span class="info-label">Servings</span>
                <span class="info-value">${recipe.servings}</span>
              </div>
            ` : ''}
            ${recipe.difficulty ? `
              <div class="info-item">
                <span class="info-label">Difficulty</span>
                <span class="info-value">${recipe.difficulty}</span>
              </div>
            ` : ''}
          </div>
          
          <h2>Ingredients</h2>
          <ul class="ingredients-list">
            ${(recipe.ingredients || []).map(ing => `
              <li>${ing.amount ? ing.amount + ' ' : ''}${ing.unit ? ing.unit + ' ' : ''}${ing.name}</li>
            `).join('')}
          </ul>
          
          <h2>Instructions</h2>
          <ol class="instructions-list">
            ${(recipe.instructions || []).map(inst => `
              <li>${inst}</li>
            `).join('')}
          </ol>
          
          ${recipe.cookingTips && recipe.cookingTips.length > 0 ? `
            <h2>Cooking Tips</h2>
            <div class="tips-list">
              <ul>
                ${recipe.cookingTips.map(tip => `<li>${tip}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          
          <div class="no-print" style="margin-top: 30px; text-align: center;">
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; background: #4caf50; color: white; border: none; border-radius: 5px; cursor: pointer;">
              Print Recipe
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; background: #666; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
              Close
            </button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDeleteRecipe = (recipeId, collectionHint) => {
    setConfirmRecipeDelete({ open: true, recipeId, collectionHint });
  };

  const executeDeleteRecipe = async (recipeId, collectionHint) => {
    try {
      console.log('Attempting to delete recipe:', recipeId, 'from', collectionHint || 'unknown', 'collection');
      
      let deleted = false;
      const deleteAttempts = [];
      
      // Try both old collection AND subcollection to handle duplicates
      try {
        console.log('Trying old collection: familyRecipes');
        await deleteDoc(doc(db, 'familyRecipes', recipeId));
        console.log('Successfully deleted from old collection');
        deleteAttempts.push('old');
        deleted = true;
      } catch (oldError) {
        console.log('Not in old collection:', oldError.code);
      }
      
      // Always try subcollection too (in case of duplicates)
      try {
        console.log('Trying subcollection: families/' + familyId + '/recipes');
        await deleteDoc(doc(db, 'families', familyId, 'recipes', recipeId));
        console.log('Successfully deleted from subcollection');
        deleteAttempts.push('subcollection');
        deleted = true;
      } catch (subError) {
        console.log('Not in subcollection:', subError.code);
      }
      
      if (!deleted) {
        throw new Error('Recipe not found in any collection');
      }
      
      console.log('Deleted from:', deleteAttempts.join(' and '));
      
      // Force refresh the recipes list
      console.log('Refreshing recipes list...');
      setRecipes([]); // Clear recipes first
      await loadRecipes();
      console.log('Recipes list refreshed');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      setSnackbarMessage('Error deleting recipe: ' + error.message);
      setSnackbarOpen(true);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImageFile(file);
    setShowImageEditor(true); // Show image editor instead of uploading immediately
  };

  const handleImageEditorSave = async (editedFile) => {
    console.log('Saving edited image:', editedFile?.name, editedFile?.size);
    setEditedImageFile(editedFile);
    setShowImageEditor(false);
    
    if (!editedFile) {
      console.error('No file provided to save');
      alert('No image file to save. Please try again.');
      return;
    }
    
    setUploading(true);
    try {
      console.log('Uploading to Firebase Storage...');
      const storageRef = ref(storage, `recipeImages/${familyId}/${Date.now()}-${editedFile.name}`);
      const snapshot = await uploadBytes(storageRef, editedFile);
      const photoUrl = await getDownloadURL(snapshot.ref);
      console.log('Photo uploaded successfully:', photoUrl);
      
      setRecipeForm(prev => ({ ...prev, photoUrl, source: prev.source || 'photo' }));
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleExtractText = async () => {
    const imageFile = editedImageFile || uploadedImageFile;
    if (!imageFile) {
      alert('Please upload and edit a recipe photo first');
      return;
    }

    setExtractingText(true);
    try {
      console.log('📖 Starting text extraction...');
      const extractedData = await extractRecipeText(imageFile);
      
      if (extractedData.success && extractedData.parsedRecipe) {
        const recipe = extractedData.parsedRecipe;
        
        // Set original servings for adjustment calculations
        const servings = parseInt(recipe.servings) || 4;
        setOriginalServings(servings);
        setServingSize(servings);
        
        // Auto-fill the form with extracted data
        setRecipeForm(prev => ({
          ...prev,
          name: recipe.name || prev.name,
          prepTime: recipe.prepTime || prev.prepTime,
          cookTime: recipe.cookTime || prev.cookTime,
          servings: recipe.servings || prev.servings,
          difficulty: recipe.difficulty || prev.difficulty,
          ingredients: recipe.ingredients.map(ing => ({
            name: ing,
            amount: '',
            unit: '',
            category: 'Pantry'
          })),
          instructions: recipe.instructions,
          source: 'photo'
        }));
        
        // Show success message with verification
        const confidence = extractedData.confidence * 100;
        const quality = recipe.ocrQuality || 'unknown';
        
        if (quality === 'poor' || confidence < 30) {
          alert(`⚠️ OCR had difficulty reading this image.\n\nExtracted: "${recipe.name}"\nQuality: ${quality}\nConfidence: ${confidence.toFixed(0)}%\n\nPlease review and edit all fields manually.`);
        } else {
          alert(`✅ Recipe extracted: "${recipe.name}"\n\nConfidence: ${confidence.toFixed(0)}%\nPlease verify the details are correct!`);
        }
        setActiveTab(0); // Switch to Basic Info tab to review
      } else if (extractedData.error) {
        alert(`OCR failed: ${extractedData.error}\nUsing fallback data - please verify everything manually.`);
      }
    } catch (error) {
      console.error('Error extracting text:', error);
      alert('Error extracting text: ' + error.message);
    } finally {
      setExtractingText(false);
    }
  };

  const handleServingSizeChange = (newServingSize) => {
    setServingSize(newServingSize);
    
    if (originalServings && newServingSize !== originalServings) {
      const adjustedIngredients = adjustRecipeServings(
        recipeForm.ingredients.map(ing => ing.name),
        originalServings,
        newServingSize
      );
      
      setRecipeForm(prev => ({
        ...prev,
        servings: newServingSize.toString(),
        ingredients: adjustedIngredients.map((adj, index) => ({
          ...prev.ingredients[index],
          name: adj.ingredient
        })),
        prepTime: adjustCookingTime(prev.prepTime, newServingSize / originalServings),
        cookTime: adjustCookingTime(prev.cookTime, newServingSize / originalServings)
      }));
    }
  };

  const addIngredient = () => {
    setRecipeForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', amount: '', unit: '', category: 'Pantry' }]
    }));
  };

  const updateIngredient = (index, field, value) => {
    setRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  const removeIngredient = (index) => {
    setRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const addInstruction = () => {
    setRecipeForm(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const updateInstruction = (index, value) => {
    setRecipeForm(prev => ({
      ...prev,
      instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
    }));
  };

  const removeInstruction = (index) => {
    setRecipeForm(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const addCookingTip = () => {
    setRecipeForm(prev => ({
      ...prev,
      cookingTips: [...prev.cookingTips, '']
    }));
  };

  const updateCookingTip = (index, value) => {
    setRecipeForm(prev => ({
      ...prev,
      cookingTips: prev.cookingTips.map((tip, i) => i === index ? value : tip)
    }));
  };

  const removeCookingTip = (index) => {
    setRecipeForm(prev => ({
      ...prev,
      cookingTips: prev.cookingTips.filter((_, i) => i !== index)
    }));
  };

  const addToShoppingList = async (recipe) => {
    if (!addShoppingItem) {
      alert('Shopping list not available. Please try from the dashboard.');
      return;
    }
    const ingredients = recipe.ingredients || [];
    if (ingredients.length === 0) {
      alert('No ingredients found in this recipe.');
      return;
    }
    try {
      let added = 0;
      for (const ing of ingredients) {
        // Build a readable ingredient string
        const parts = [];
        if (ing.amount) parts.push(ing.amount);
        if (ing.unit) parts.push(ing.unit);
        if (ing.name) parts.push(ing.name);
        const itemName = parts.length > 0 ? parts.join(' ') : (typeof ing === 'string' ? ing : '');
        if (!itemName.trim()) continue;
        await addShoppingItem({
          name: itemName.trim(),
          item: itemName.trim(),
          quantity: 1,
          category: ing.category || 'Recipe Ingredients',
          price: 0,
          status: 'pending',
          store: 'amazon',
          completed: false,
          addedBy: currentUser?.uid || 'unknown',
          requestedBy: `Recipe: ${recipe.name || recipe.title || 'Unknown'}`,
          addedAt: new Date().toISOString()
        });
        added++;
      }
      alert(`✅ Added ${added} ingredient${added !== 1 ? 's' : ''} from "${recipe.name || recipe.title}" to your shopping list!`);
    } catch (error) {
      console.error('Error adding to shopping list:', error);
      alert('Error adding ingredients. Please try again.');
    }
  };

  const handleShareRecipe = async (recipe) => {
    console.log('🔗 Share button clicked for recipe:', {
      id: recipe.id,
      name: recipe.name,
      hasPhoto: !!recipe.photoUrl
    });
    
    const result = await shareRecipeLink(recipe);
    console.log('📤 Share result:', result);
    
    if (result.success) {
      const message = result.method === 'clipboard' 
        ? `Link copied! ${result.url || ''}` 
        : 'Recipe shared!';
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    } else if (result.cancelled) {
      console.log('⚠️ User cancelled share');
      // Don't show error for cancellation
    } else {
      setSnackbarMessage('Failed to share recipe.');
      setSnackbarOpen(true);
    }
  };

  const ingredientCategories = [
    'Produce', 'Dairy', 'Meat & Seafood', 'Bakery', 'Pantry', 
    'Snacks', 'Beverages', 'Frozen', 'Personal Care', 'Household'
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              startIcon={<ArrowBack />}
            >
              Back to Dashboard
            </Button>
          )}
          <Typography variant="h4" fontWeight="bold" color="primary">
            🍳 Family Recipe Collection
          </Typography>
        </Box>
        <Fab
          color="primary"
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: 'primary.main' }}
        >
          <Add />
        </Fab>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {recipes.map((recipe) => (
          <Grid item xs={12} md={6} lg={4} key={recipe.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {recipe.photoUrl && (
                <Box
                  component="img"
                  src={recipe.photoUrl}
                  alt={recipe.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover'
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {recipe.name}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {recipe.prepTime && (
                    <Chip icon={<Timer />} label={`Prep: ${recipe.prepTime}`} size="small" />
                  )}
                  {recipe.cookTime && (
                    <Chip icon={<Timer />} label={`Cook: ${recipe.cookTime}`} size="small" />
                  )}
                  {recipe.servings && (
                    <Chip icon={<People />} label={`Serves: ${recipe.servings}`} size="small" />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {recipe.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={recipe.difficulty} size="small" color="primary" />
                  {recipe.source === 'photo' && <Chip label="📸 Photo" size="small" />}
                  {recipe.source === 'manual' && <Chip label="✍️ Handwritten" size="small" />}
                </Box>
              </CardContent>
              
              <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<LocalGroceryStore />}
                  onClick={() => addToShoppingList(recipe)}
                  variant="outlined"
                >
                  Add to Shopping
                </Button>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => handleOpenDialog(recipe)}
                >
                  View
                </Button>
                <IconButton
                  size="small"
                  onClick={() => handleShareRecipe(recipe)}
                  color="primary"
                  title="Share Recipe"
                >
                  <Share />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handlePrintRecipe(recipe)}
                  color="primary"
                  title="Print Recipe"
                >
                  <Print />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRecipe(recipe.id, recipe._collection)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {recipes.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
          <MenuBook sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No recipes yet!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start building your family's recipe collection by adding your favorite dishes.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Your First Recipe
          </Button>
        </Paper>
      )}

      {/* Recipe Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingRecipe && (
              <Button
                size="small"
                variant={isEditMode ? 'contained' : 'outlined'}
                onClick={() => setIsEditMode(prev => !prev)}
                sx={{ minWidth: 70 }}
              >
                {isEditMode ? '✏️ Editing' : '✏️ Edit'}
              </Button>
            )}
            <Typography variant="h6" component="span">
              {editingRecipe ? (isEditMode ? 'Edit Recipe' : recipeForm.name || 'View Recipe') : 'Add New Recipe'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)} 
                  sx={{ mb: 3 }}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  <Tab label="Basic Info" />
                  <Tab label="Ingredients" />
                  <Tab label="Instructions" />
                  <Tab label="Photo & Tips" />
                </Tabs>

          {/* Basic Info Tab */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Recipe Name"
                value={recipeForm.name}
                onChange={(e) => setRecipeForm(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={recipeForm.description}
                onChange={(e) => setRecipeForm(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
                fullWidth
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Prep Time"
                    value={recipeForm.prepTime}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, prepTime: e.target.value }))}
                    placeholder="e.g., 15 minutes"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Cook Time"
                    value={recipeForm.cookTime}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, cookTime: e.target.value }))}
                    placeholder="e.g., 30 minutes"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Servings"
                    value={recipeForm.servings}
                    onChange={(e) => {
                      const newServings = parseInt(e.target.value) || 4;
                      setRecipeForm(prev => ({ ...prev, servings: e.target.value }));
                      handleServingSizeChange(newServings);
                    }}
                    placeholder="e.g., 4-6"
                    fullWidth
                  />
                  {originalServings > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Original: {originalServings} servings
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" gutterBottom>
                          Quick Adjust:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Button size="small" variant="outlined" onClick={() => handleServingSizeChange(Math.max(1, servingSize - 1))}>-1</Button>
                          <Button size="small" variant="outlined" onClick={() => handleServingSizeChange(originalServings)}>Reset</Button>
                          <Button size="small" variant="outlined" onClick={() => handleServingSizeChange(servingSize + 1)}>+1</Button>
                          <Button size="small" variant="outlined" onClick={() => handleServingSizeChange(Math.round(originalServings * 2))}>2x</Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Difficulty</InputLabel>
                    <Select
                      value={recipeForm.difficulty}
                      onChange={(e) => setRecipeForm(prev => ({ ...prev, difficulty: e.target.value }))}
                      label="Difficulty"
                    >
                      <MenuItem value="Easy">Easy</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Hard">Hard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Ingredients Tab */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Ingredients</Typography>
                {isEditMode && (
                  <Button startIcon={<Add />} onClick={addIngredient}>
                    Add Ingredient
                  </Button>
                )}
              </Box>

              {/* VIEW MODE: clean bullet list */}
              {!isEditMode && (
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {recipeForm.ingredients.filter(ing => ing.name || (typeof ing === 'string' && ing)).map((ingredient, index) => {
                    const parts = [];
                    if (typeof ingredient === 'string') {
                      return <Box component="li" key={index} sx={{ mb: 1 }}><Typography variant="body1">{ingredient}</Typography></Box>;
                    }
                    if (ingredient.amount) parts.push(ingredient.amount);
                    if (ingredient.unit) parts.push(ingredient.unit);
                    if (ingredient.name) parts.push(ingredient.name);
                    return (
                      <Box component="li" key={index} sx={{ mb: 1 }}>
                        <Typography variant="body1">{parts.join(' ')}</Typography>
                      </Box>
                    );
                  })}
                  {recipeForm.ingredients.filter(ing => ing.name).length === 0 && (
                    <Typography color="text.secondary">No ingredients yet. Click Edit to add them.</Typography>
                  )}
                </Box>
              )}

              {/* EDIT MODE: form fields */}
              {isEditMode && recipeForm.ingredients.map((ingredient, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Ingredient Name"
                        value={ingredient.name}
                        onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                        fullWidth
                        multiline
                        minRows={1}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        label="Amount"
                        value={ingredient.amount}
                        onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                        placeholder="2"
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        label="Unit"
                        value={ingredient.unit}
                        onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                        placeholder="cups"
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={ingredient.category}
                          onChange={(e) => updateIngredient(index, 'category', e.target.value)}
                          label="Category"
                        >
                          {ingredientCategories.map(category => (
                            <MenuItem key={category} value={category}>{category}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => removeIngredient(index)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          )}

          {/* Instructions Tab */}

          {activeTab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Instructions</Typography>
                {isEditMode && (
                  <Button startIcon={<Add />} onClick={addInstruction}>
                    Add Step
                  </Button>
                )}
              </Box>

              {/* VIEW MODE: clean numbered list — no scroll boxes */}
              {!isEditMode && (
                <Box>
                  {recipeForm.instructions.filter(s => s.trim()).map((instruction, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        p: 2,
                        bgcolor: index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderColor: 'primary.main'
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ minWidth: 32, color: 'primary.main', fontWeight: 'bold', flexShrink: 0 }}
                      >
                        {index + 1}.
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {instruction}
                      </Typography>
                    </Box>
                  ))}
                  {recipeForm.instructions.filter(s => s.trim()).length === 0 && (
                    <Typography color="text.secondary">No instructions yet. Click Edit to add them.</Typography>
                  )}
                </Box>
              )}

              {/* EDIT MODE: text fields */}
              {isEditMode && recipeForm.instructions.map((instruction, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="h6" sx={{ minWidth: 32, color: 'primary.main', flexShrink: 0 }}>
                      {index + 1}.
                    </Typography>
                    <TextField
                      label={`Step ${index + 1}`}
                      value={instruction}
                      onChange={(e) => updateInstruction(index, e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
                    />
                    <IconButton
                      onClick={() => removeInstruction(index)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {/* Photo & Tips Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>Recipe Photo</Typography>
              <Box sx={{ mb: 3 }}>
                {/* File Upload */}
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="recipe-photo-upload"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                {/* Camera Capture */}
                <input
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  id="recipe-photo-camera"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <label htmlFor="recipe-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : '📁 Upload Photo'}
                    </Button>
                  </label>
                  
                  <label htmlFor="recipe-photo-camera">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CameraAlt />}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : '📷 Take Photo'}
                    </Button>
                  </label>
                </Box>
                {recipeForm.photoUrl && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={recipeForm.photoUrl}
                      alt="Recipe"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExtractText}
                        disabled={extractingText}
                        startIcon={<Visibility />}
                      >
                        {extractingText ? 'Extracting...' : '🤖 Extract Text'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowImageEditor(true)}
                        disabled={!uploadedImageFile}
                        startIcon={<Crop />}
                      >
                        ✂️ Edit Image
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        AI will read the recipe and fill in the form
                      </Typography>
                      <Typography variant="caption" color="info.main" sx={{ alignSelf: 'center', mt: 1, display: 'block' }}>
                        💡 Tip: For best results, ensure text is clear and well-lit. Crop out unnecessary background.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Cooking Tips</Typography>
                  <Button startIcon={<Add />} onClick={addCookingTip}>
                    Add Tip
                  </Button>
                </Box>
                
                {recipeForm.cookingTips.map((tip, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label={`Cooking Tip ${index + 1}`}
                        value={tip}
                        onChange={(e) => updateCookingTip(index, e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        placeholder="e.g., Check chicken temperature reaches 165°F"
                      />
                      <IconButton
                        onClick={() => removeCookingTip(index)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button type="button" onClick={handleCloseDialog} disabled={uploading}>
            {isEditMode ? 'Cancel' : 'Close'}
          </Button>
          {!isEditMode && editingRecipe && (
            <Button
              type="button"
              variant="outlined"
              startIcon={<Save />}
              onClick={() => setIsEditMode(true)}
            >
              Edit Recipe
            </Button>
          )}
          {isEditMode && (
            <Button
              type="button"
              onClick={handleSaveRecipe}
              variant="contained"
              startIcon={<Save />}
              disabled={uploading}
            >
              {uploading ? 'Saving...' : 'Save Recipe'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Editor Dialog */}
      <ImageEditor
        open={showImageEditor}
        onClose={() => setShowImageEditor(false)}
        onSave={handleImageEditorSave}
        imageFile={uploadedImageFile}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirm Delete Recipe Dialog */}
      <Dialog open={confirmRecipeDelete.open} onClose={() => setConfirmRecipeDelete({ open: false, recipeId: null, collectionHint: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Recipe?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this recipe? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRecipeDelete({ open: false, recipeId: null, collectionHint: null })} sx={{ minHeight: 48 }}>Cancel</Button>
          <Button variant="contained" color="error" sx={{ minHeight: 48 }} onClick={() => {
            const { recipeId, collectionHint } = confirmRecipeDelete;
            setConfirmRecipeDelete({ open: false, recipeId: null, collectionHint: null });
            executeDeleteRecipe(recipeId, collectionHint);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RecipeManager;
